import { z } from 'zod'

const taskStatusSchema = z.object({
  id: z.string(), position: z.number(), title: z.string(), kind: z.string().optional(), status: z.string(),
  detail: z.string().nullable().optional(), wait_reason: z.string().nullable().optional(),
  proof_level: z.string().optional(), next_action: z.string().nullable().optional(),
  blocker: z.string().nullable().optional(), origin_run_id: z.string().nullable().optional(),
  // Every run authorised to work on this task, oldest first. The run that
  // created it is only the first entry; grouping and live attachment key on the
  // whole list so a resumed task renders under the run currently doing the work.
  touched_run_ids: z.array(z.string()).optional(),
  latest_run_id: z.string().nullable().optional(),
  // Whether this kind of work can survive a response boundary, and the
  // machine-readable reason it is in its current settled state.
  resumable: z.boolean().optional(),
  settle_reason: z.string().nullable().optional(),
  // Who may say this task is done. `server` is only ever completed by a
  // provider confirmation bound to an approval; `model` is the agent's own
  // account. The two must not render alike.
  authority: z.enum(['model', 'server']).optional(),
  // The agent's handle for a task it wrote. Its presence is what marks a
  // snapshot as agent-authored rather than planner-created.
  client_key: z.string().nullable().optional(),
  // Execution order for a task advanced by a status transition alone, which
  // produces no activity row of its own.
  first_progress_seq: z.number().nullable().optional(),
  created_at: z.string().nullable().optional(), updated_at: z.string().nullable().optional(),
})

const workActivitySchema = z.object({
  id: z.string(), task_id: z.string().nullable().optional(), kind: z.string().optional(), label: z.string(),
  status: z.enum(['running', 'succeeded', 'failed', 'superseded']).catch('running'),
  tool_slug: z.string().nullable().optional(), origin_run_id: z.string().nullable().optional(),
  attempt_id: z.string().nullable().optional(), started_at: z.string().nullable().optional(),
  finished_at: z.string().nullable().optional(), summary: z.string().nullable().optional(),
  // `bound` is an explicit agent selection the server re-validated; `matched`
  // is the legacy tool-kind guess it replaces.
  attribution: z.enum(['matched', 'inferred', 'bound']).nullable().optional(),
  // Only comparable alongside `origin_run_id`: SSE sequence restarts each run
  // while the checkpoint spans the conversation.
  started_seq: z.number().nullable().optional(), last_seq: z.number().nullable().optional(),
})

export const workStatusCardSchema = z.object({
  schema_version: z.number().optional(), revision: z.number(), tasks: z.array(taskStatusSchema),
  activity: z.array(workActivitySchema).optional(),
  // The only signal by which a client may drop a row it holds. Never infer
  // eviction from sequence ranges: a live row whose checkpoint write failed has
  // no sequence the server ever saw.
  activity_evicted_ids: z.array(z.string()).optional(),
  // Settled tasks archived to keep a long conversation under the task cap.
  // Summaries only — they carry no activity and cannot come back.
  archived_tasks: z.array(z.object({ id: z.string(), title: z.string() }).passthrough()).optional(),
})
